import React, { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotesIcon from "@mui/icons-material/Notes";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  useCreateShopMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import { uploadImage } from "../../../Components/UploadFile/upload";

// ─── Phone helpers ────────────────────────────────────────────────────────────
const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const stripPhone = (formatted: string): string =>
  formatted.replace(/\D/g, "");

const validatePhone = (value: string): string | undefined => {
  if (!value) return undefined;
  const digits = stripPhone(value);
  if (digits.length > 0 && digits.length !== 10)
    return "Phone number must be 10 digits — e.g. (416) 555-1234";
  return undefined;
};

// ─── Email helper ─────────────────────────────────────────────────────────────
const validateEmail = (value: string): string | undefined => {
  if (!value) return undefined;
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  return ok ? undefined : "Invalid email address";
};

// ─── Nominatim address suggestion type ────────────────────────────────────────
interface NominatimResult {
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
  };
}

// ─── Preferred call time slots ────────────────────────────────────────────────
const TIME_SLOTS: string[] = [
  "Anytime",
  "Early Morning (7am – 9am)",
  "Morning (9am – 12pm)",
  "Afternoon (12pm – 3pm)",
  "Late Afternoon (3pm – 6pm)",
  "Evening (6pm – 9pm)",
  // specific hours
  "7:00 AM", "7:30 AM",
  "8:00 AM", "8:30 AM",
  "9:00 AM", "9:30 AM",
  "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM",
  "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM",
  "6:00 PM", "6:30 PM",
  "7:00 PM", "7:30 PM",
  "8:00 PM",
];

// ─── Section header component ─────────────────────────────────────────────────
const SectionHeader = ({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 3,
      py: 2,
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: (t) =>
        t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: "8px",
        bgcolor: "primary.main",
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography fontWeight={600} fontSize={14} color="text.primary">
      {title}
    </Typography>
  </Box>
);

// ─── Field wrapper ────────────────────────────────────────────────────────────
const Field = ({
  label,
  required,
  children,
  cols = 6,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  cols?: number;
}) => (
  <Grid item lg={cols} md={cols} sm={12} xs={12}>
    <Typography variant="body2" fontWeight={500} mb={0.5} color="text.secondary">
      {label}
      {required && (
        <Box component="span" color="error.main" ml={0.3}>
          *
        </Box>
      )}
    </Typography>
    {children}
  </Grid>
);

// ─── Main component ───────────────────────────────────────────────────────────
const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [createShop, { isLoading }] = useCreateShopMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Address autocomplete state
  const [addressOptions, setAddressOptions] = useState<NominatimResult[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("");
  const [addressOpen, setAddressOpen] = useState(false);
  const addressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = async (query: string) => {
    if (query.trim().length < 4) {
      setAddressOptions([]);
      setAddressOpen(false);
      return;
    }
    setAddressLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query
        )}&format=json&addressdetails=1&countrycodes=ca&limit=6`,
        { headers: { "Accept-Language": "en", "User-Agent": "POS-App/1.0" } }
      );
      const data: NominatimResult[] = await res.json();
      setAddressOptions(data);
      setAddressOpen(data.length > 0);
    } catch {
      setAddressOptions([]);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    if (addressTimer.current) clearTimeout(addressTimer.current);
    addressTimer.current = setTimeout(() => searchAddress(value), 450);
  };

  const handleAddressSelect = (result: NominatimResult) => {
    const a = result.address;
    formik.setFieldValue("customerStreetNumber", a.house_number || "");
    formik.setFieldValue("customerStreetName", a.road || "");
    formik.setFieldValue("customerCity", a.city || a.town || a.village || "");
    formik.setFieldValue("customerProvince", a.state || "");
    formik.setFieldValue("customerPostalCode", a.postcode || "");
    setAddressInput(result.display_name);
    setAddressOptions([]);
    setAddressOpen(false);
  };

  // ─── Formik ─────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      customerFirstName: "",
      customerLastName: "",
      customerMobileNum: "",
      customerEmail: "",
      customerProvince: "",
      customerCity: "",
      customerPostalCode: "",
      customerStreetNumber: "",
      customerStreetName: "",
      customerDescription: "",
      customerCompanyName: "",
      customerCompanyPhoneNumebr: "",
      customerCompanyEmail: "",
      customerPreferTimeToCall: "",
      customerImg: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      const e1 = validateEmail(values.customerEmail);
      if (e1) errors.customerEmail = e1;
      const e2 = validateEmail(values.customerCompanyEmail);
      if (e2) errors.customerCompanyEmail = e2;
      const e3 = validatePhone(values.customerMobileNum);
      if (e3) errors.customerMobileNum = e3;
      const e4 = validatePhone(values.customerCompanyPhoneNumebr);
      if (e4) errors.customerCompanyPhoneNumebr = e4;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let customerImg: string | undefined;
        if (selectedImage) {
          setIsImageUploading(true);
          customerImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const payload = {
          ...values,
          customerMobileNum: stripPhone(values.customerMobileNum),
          customerCompanyPhoneNumebr: stripPhone(values.customerCompanyPhoneNumebr),
          customerImg,
        };

        const res = await createShop(payload).unwrap();
        if (!res.status) {
          showErrorMessage(res.message);
        } else {
          showMessage("Restaurant / Shop created successfully");
          navigate("/shop/list");
          resetForm();
          setSelectedImage(undefined);
          setAddressInput("");
          setAddressOptions([]);
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    const { customerFirstName, customerLastName, customerMobileNum } = formik.values;
    return (
      customerFirstName.trim() !== "" &&
      customerLastName.trim() !== "" &&
      customerMobileNum.trim() !== ""
    );
  }, [formik.values]);

  // ─── Phone change handler ─────────────────────────────────────────────────
  const handlePhoneChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(field, formatPhone(e.target.value));
    };

  // ─── Image handling ───────────────────────────────────────────────────────
  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d")!;
          let { width, height } = img;
          if (width > height ? width > maxW : height > maxH) {
            if (width > height) { height *= maxW / width; width = maxW; }
            else { width *= maxH / height; height = maxH; }
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) resolve(new File([blob], file.name, { type: file.type }));
            else reject(new Error("Canvas empty"));
          }, file.type);
        };
        img.onerror = reject;
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return setSelectedImage(undefined);
    const valid = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    if (!valid.includes(file.type))
      return showErrorMessage("File must be an image (jpeg, png, jpg, gif).");
    if (file.size > 2048 * 1024)
      return showErrorMessage("File must be smaller than 2 MB.");
    try {
      setSelectedImage(await resizeImage(file, 500, 500));
    } catch {
      showErrorMessage("Failed to process image.");
    }
  };

  const txProps = {
    size: "small" as const,
    fullWidth: true,
    InputProps: { sx: { fontSize: 14, borderRadius: "8px" } },
  };

  return (
    <Grid container spacing={3}>
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <Grid item lg={3} md={3} sm={12} xs={12}>
        <Card sx={{ borderRadius: 2, position: "sticky", top: 24 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Add Restaurant
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
              Fill in the restaurant or shop details. Fields marked with{" "}
              <Box component="span" color="error.main" fontWeight={600}>*</Box> are required.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Quick checklist */}
            {[
              { label: "Company info", done: !!formik.values.customerCompanyName },
              { label: "Contact person", done: !!formik.values.customerFirstName && !!formik.values.customerLastName },
              { label: "Phone number", done: stripPhone(formik.values.customerMobileNum).length === 10 },
              { label: "Address", done: !!formik.values.customerCity },
            ].map(({ label, done }) => (
              <Box key={label} display="flex" alignItems="center" gap={1} mb={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: done ? "success.main" : "grey.300",
                    transition: "background-color 0.3s",
                  }}
                />
                <Typography variant="body2" color={done ? "success.main" : "text.disabled"}>
                  {label}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      {/* ── Form ────────────────────────────────────────────────────────── */}
      <Grid item lg={9} md={9} sm={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>

            {/* ── Profile image ─────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader icon={<CameraAltIcon fontSize="small" />} title="Profile Photo" />
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Box sx={{ position: "relative", flexShrink: 0 }}>
                      <Avatar
                        src={selectedImage ? URL.createObjectURL(selectedImage) : undefined}
                        sx={{ width: 88, height: 88, bgcolor: "primary.light", fontSize: 32 }}
                      >
                        {formik.values.customerCompanyName?.[0]?.toUpperCase() ||
                          formik.values.customerFirstName?.[0]?.toUpperCase() || "S"}
                      </Avatar>
                      {selectedImage && (
                        <Tooltip title="Remove photo">
                          <IconButton
                            size="small"
                            onClick={() => setSelectedImage(undefined)}
                            sx={{
                              position: "absolute",
                              top: -6,
                              right: -6,
                              bgcolor: "error.main",
                              color: "#fff",
                              width: 22,
                              height: 22,
                              "&:hover": { bgcolor: "error.dark" },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box>
                      <input
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        id="shop-img-upload"
                        type="file"
                        onChange={handleImageChange}
                      />
                      <label htmlFor="shop-img-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CameraAltIcon />}
                          size="small"
                          sx={{ borderRadius: "8px", textTransform: "none" }}
                        >
                          {selectedImage ? "Change Photo" : "Upload Photo"}
                        </Button>
                      </label>
                      <Typography variant="caption" display="block" color="text.disabled" mt={0.5}>
                        JPG, PNG or GIF — max 2 MB
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Company info ──────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader icon={<BusinessIcon fontSize="small" />} title="Restaurant Details" />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>
                    <Field label="Restaurant Name" cols={12}>
                      <TextField
                        placeholder="e.g. The Golden Fork Restaurant"
                        {...txProps}
                        {...formik.getFieldProps("customerCompanyName")}
                        InputProps={{
                          ...txProps.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Field>

                    <Field label="Company Phone">
                      <TextField
                        placeholder="(416) 555-1234"
                        {...txProps}
                        value={formik.values.customerCompanyPhoneNumebr}
                        onChange={handlePhoneChange("customerCompanyPhoneNumebr")}
                        onBlur={() => formik.setFieldTouched("customerCompanyPhoneNumebr")}
                        error={
                          formik.touched.customerCompanyPhoneNumebr &&
                          !!formik.errors.customerCompanyPhoneNumebr
                        }
                        helperText={
                          formik.touched.customerCompanyPhoneNumebr &&
                          formik.errors.customerCompanyPhoneNumebr
                        }
                        InputProps={{
                          ...txProps.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: formik.values.customerCompanyPhoneNumebr &&
                            stripPhone(formik.values.customerCompanyPhoneNumebr).length === 10 ? (
                            <InputAdornment position="end">
                              <Chip label="Valid" size="small" color="success" variant="outlined" />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Field>

                    <Field label="Company Email">
                      <TextField
                        placeholder="info@restaurant.com"
                        type="email"
                        {...txProps}
                        {...formik.getFieldProps("customerCompanyEmail")}
                        error={
                          formik.touched.customerCompanyEmail &&
                          !!formik.errors.customerCompanyEmail
                        }
                        helperText={
                          formik.touched.customerCompanyEmail &&
                          formik.errors.customerCompanyEmail
                        }
                        InputProps={{
                          ...txProps.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Field>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Address ───────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader icon={<LocationOnIcon fontSize="small" />} title="Address" />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>

                    {/* Address search / autocomplete */}
                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight={500} mb={0.5} color="text.secondary">
                        Search Address
                      </Typography>
                      <Box sx={{ position: "relative" }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Start typing an address to auto-fill fields below..."
                          value={addressInput}
                          onChange={(e) => handleAddressInputChange(e.target.value)}
                          onBlur={() => setTimeout(() => setAddressOpen(false), 200)}
                          onFocus={() => addressOptions.length > 0 && setAddressOpen(true)}
                          InputProps={{
                            sx: { fontSize: 14, borderRadius: "8px" },
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: addressLoading ? (
                              <InputAdornment position="end">
                                <CircularProgress size={16} />
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                        {addressOpen && addressOptions.length > 0 && (
                          <Paper
                            elevation={4}
                            sx={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 1300,
                              maxHeight: 260,
                              overflowY: "auto",
                              mt: 0.5,
                              borderRadius: "8px",
                            }}
                          >
                            {addressOptions.map((opt, idx) => (
                              <Box
                                key={idx}
                                onMouseDown={() => handleAddressSelect(opt)}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                  px: 2,
                                  py: 1.2,
                                  cursor: "pointer",
                                  fontSize: 13,
                                  borderBottom: idx < addressOptions.length - 1 ? "1px solid" : "none",
                                  borderColor: "divider",
                                  "&:hover": { bgcolor: "action.hover" },
                                }}
                              >
                                <LocationOnIcon
                                  fontSize="small"
                                  sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }}
                                />
                                <Typography variant="body2" lineHeight={1.4}>
                                  {opt.display_name}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.disabled">
                        Selecting a result will auto-fill the fields below
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider>
                        <Typography variant="caption" color="text.disabled">
                          or fill manually
                        </Typography>
                      </Divider>
                    </Grid>

                    <Field label="Street No." cols={3}>
                      <TextField
                        placeholder="123"
                        {...txProps}
                        {...formik.getFieldProps("customerStreetNumber")}
                      />
                    </Field>

                    <Field label="Street Name" cols={9}>
                      <TextField
                        placeholder="Main Street"
                        {...txProps}
                        {...formik.getFieldProps("customerStreetName")}
                      />
                    </Field>

                    <Field label="City">
                      <TextField
                        placeholder="Toronto"
                        {...txProps}
                        {...formik.getFieldProps("customerCity")}
                      />
                    </Field>

                    <Field label="Province">
                      <TextField
                        placeholder="Ontario"
                        {...txProps}
                        {...formik.getFieldProps("customerProvince")}
                      />
                    </Field>

                    <Field label="Postal Code" cols={4}>
                      <TextField
                        placeholder="M5V 2T6"
                        {...txProps}
                        {...formik.getFieldProps("customerPostalCode")}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "customerPostalCode",
                            e.target.value.toUpperCase()
                          )
                        }
                      />
                    </Field>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Contact person ────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader icon={<PersonIcon fontSize="small" />} title="Contact Person" />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>
                    <Field label="First Name" required>
                      <TextField
                        placeholder="John"
                        {...txProps}
                        {...formik.getFieldProps("customerFirstName")}
                      />
                    </Field>

                    <Field label="Last Name" required>
                      <TextField
                        placeholder="Smith"
                        {...txProps}
                        {...formik.getFieldProps("customerLastName")}
                      />
                    </Field>

                    <Field label="Mobile Number" required>
                      <TextField
                        placeholder="(416) 555-1234"
                        {...txProps}
                        value={formik.values.customerMobileNum}
                        onChange={handlePhoneChange("customerMobileNum")}
                        onBlur={() => formik.setFieldTouched("customerMobileNum")}
                        error={
                          formik.touched.customerMobileNum &&
                          !!formik.errors.customerMobileNum
                        }
                        helperText={
                          formik.touched.customerMobileNum &&
                          formik.errors.customerMobileNum
                        }
                        InputProps={{
                          ...txProps.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: formik.values.customerMobileNum &&
                            stripPhone(formik.values.customerMobileNum).length === 10 ? (
                            <InputAdornment position="end">
                              <Chip label="Valid" size="small" color="success" variant="outlined" />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Field>

                    <Field label="Email">
                      <TextField
                        placeholder="john@restaurant.com"
                        type="email"
                        {...txProps}
                        {...formik.getFieldProps("customerEmail")}
                        error={
                          formik.touched.customerEmail && !!formik.errors.customerEmail
                        }
                        helperText={
                          formik.touched.customerEmail && formik.errors.customerEmail
                        }
                        InputProps={{
                          ...txProps.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Field>

                    <Field label="Preferred Time to Call" cols={12}>
                      <Select
                        fullWidth
                        size="small"
                        displayEmpty
                        value={formik.values.customerPreferTimeToCall}
                        onChange={(e) =>
                          formik.setFieldValue("customerPreferTimeToCall", e.target.value)
                        }
                        startAdornment={
                          <InputAdornment position="start">
                            <AccessTimeIcon fontSize="small" color="action" />
                          </InputAdornment>
                        }
                        sx={{ fontSize: 14, borderRadius: "8px" }}
                        renderValue={(val) =>
                          val ? (
                            <Typography fontSize={14}>{val as string}</Typography>
                          ) : (
                            <Typography fontSize={14} color="text.disabled">
                              Select preferred call time
                            </Typography>
                          )
                        }
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300, borderRadius: "8px" } } }}
                      >
                        <MenuItem value="" sx={{ color: "text.disabled", fontSize: 13 }}>
                          — Select time —
                        </MenuItem>
                        <MenuItem disabled sx={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>
                          TIME RANGES
                        </MenuItem>
                        {TIME_SLOTS.slice(0, 6).map((t) => (
                          <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                            {t}
                          </MenuItem>
                        ))}
                        <MenuItem disabled sx={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>
                          SPECIFIC TIMES
                        </MenuItem>
                        {TIME_SLOTS.slice(6).map((t) => (
                          <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </Field>

                    <Field label="Notes / Description" cols={12}>
                      <TextField
                        placeholder="Any special instructions, delivery notes, or comments..."
                        {...txProps}
                        multiline
                        rows={4}
                        {...formik.getFieldProps("customerDescription")}
                        InputProps={{
                          sx: { fontSize: 14, borderRadius: "8px", alignItems: "flex-start" },
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mt: 1.2 }}>
                              <NotesIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Field>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Actions ───────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="flex-end" gap={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      onClick={() => {
                        formik.resetForm();
                        setSelectedImage(undefined);
                        setAddressInput("");
                        setAddressOptions([]);
                        setAddressOpen(false);
                      }}
                      sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        isLoading || isImageUploading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <SaveAltIcon />
                        )
                      }
                      onClick={() => formik.handleSubmit()}
                      disabled={!formValid || isLoading || isImageUploading}
                      sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                    >
                      {isLoading || isImageUploading ? "Saving..." : "Save Restaurant"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default Index;
